'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId, MarketId } from '@morpho-org/blue-sdk';
import { getIsPositionUnhealthy, TOU_URL } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Button } from '@repo/ui';
import { ArrowDiagonalUp, Close } from '@repo/ui/icons';
import { memo, useMemo, useState } from 'react';
import { AUTO_DELEVERAGE_DOCS_URL } from '@/config';
import { useMarketBlue } from '@/hooks/operation/market/v2/useMarketBlue';
import CloseToLiquidationConfirmBox from '../../CloseToLiquidationConfirmBox/CloseToLiquidationConfirmBox';
import { useAutoDeleverage } from '../hooks/useAutoDeleverage';
import AutoDeleverageDialogDetails from './AutoDeleverageDialogDetails';
import AutoDeleverageReviewDialog from './AutoDeleverageReviewDialog';
interface AutoDeleverageDialogProps {
  marketId: MarketId;
  chainId: ChainId;
  toEnable: boolean;
}
const AutoDeleverageDialog = t0 => {
  const $ = _c(51);
  const {
    marketId,
    chainId,
    toEnable
  } = t0;
  const {
    toggleDialog
  } = useDialogContext();
  const {
    preLltv,
    marketParams
  } = useAutoDeleverage(marketId, chainId);
  const [isPositionRiskyAcknowledged, setIsPositionRiskyAcknowledged] = useState(false);
  const {
    positionData: initialPosition
  } = useMarketBlue(marketParams);
  let t1;
  if ($[0] !== initialPosition?.ltv || $[1] !== preLltv) {
    t1 = isDefined(initialPosition?.ltv) && isDefined(preLltv) && initialPosition?.ltv >= preLltv;
    $[0] = initialPosition?.ltv;
    $[1] = preLltv;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isAbovePreLltv = t1;
  initialPosition?.ltv;
  let t2;
  if ($[3] !== initialPosition?.ltv || $[4] !== preLltv) {
    t2 = isDefined(initialPosition?.ltv) && isDefined(preLltv) && getIsPositionUnhealthy(initialPosition?.ltv, preLltv);
    $[3] = initialPosition?.ltv;
    $[4] = preLltv;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const isPositionRisky = t2;
  const needToAcknowledgePositionRisky = isPositionRisky && !isPositionRiskyAcknowledged;
  let t3;
  if ($[6] !== preLltv) {
    t3 = formatters.percent.digits(2).default("-").trailingZero(false).unit("%").of(preLltv, 18);
    $[6] = preLltv;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const formattedPreLltv = t3;
  let t4;
  if ($[8] !== chainId || $[9] !== marketId || $[10] !== toEnable || $[11] !== toggleDialog) {
    t4 = () => {
      toggleDialog(<AutoDeleverageReviewDialog toEnable={toEnable} marketId={marketId} chainId={chainId} />);
    };
    $[8] = chainId;
    $[9] = marketId;
    $[10] = toEnable;
    $[11] = toggleDialog;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const openReviewDialog = t4;
  const t5 = toEnable ? "Enable" : "Disable";
  let t6;
  if ($[13] !== t5) {
    t6 = <span className="text-title-sm">{t5} Auto-Deleverage</span>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== toggleDialog) {
    t7 = () => toggleDialog(false);
    $[15] = toggleDialog;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Close />;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7) {
    t9 = <Button variant="secondary" size="icon" aria-label="Close" onClick={t7}>{t8}</Button>;
    $[18] = t7;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t6 || $[21] !== t9) {
    t10 = <div className="gap-sm flex items-center justify-between">{t6}{t9}</div>;
    $[20] = t6;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== toEnable) {
    t11 = toEnable ? <span className="text-secondary-foreground text-xs">Auto-Deleverage is a feature designed to reduce the risk of full liquidation. When enabled, if your LTV exceeds a pre-set threshold, a portion of your position may be closed to bring it back to safer levels. This mechanism relies on on-chain liquidators and may not keep up with rapid market movements.</span> : <span className="text-secondary-foreground text-xs">Auto-Deleverage is being sunset and can no longer be enabled for new positions. Your position will remain enabled for auto-deleverage onchain, but the feature will stop being maintained and users will need to monitor their positions.</span>;
    $[23] = toEnable;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const t12 = initialPosition?.ltv ?? null;
  let t13;
  if ($[25] !== isPositionRisky || $[26] !== marketId || $[27] !== t12) {
    t13 = <AutoDeleverageDialogDetails marketId={marketId} ltv={t12} isPositionRisky={isPositionRisky} />;
    $[25] = isPositionRisky;
    $[26] = marketId;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== formattedPreLltv || $[30] !== isAbovePreLltv || $[31] !== toEnable) {
    t14 = toEnable && isAbovePreLltv && <div className="gap-xs p-xs bg-destructive-foreground/18 flex items-center justify-center rounded-[4px]"><span className="text-2xs text-destructive-foreground">You must lower your LTV below {formattedPreLltv} to activate Auto-Deleverage.</span></div>;
    $[29] = formattedPreLltv;
    $[30] = isAbovePreLltv;
    $[31] = toEnable;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== isAbovePreLltv || $[34] !== isPositionRisky || $[35] !== isPositionRiskyAcknowledged) {
    t15 = !isAbovePreLltv && isPositionRisky && <CloseToLiquidationConfirmBox isRiskyPositionsAcknowledged={isPositionRiskyAcknowledged} setIsRiskyPositionsAcknowledged={setIsPositionRiskyAcknowledged} />;
    $[33] = isAbovePreLltv;
    $[34] = isPositionRisky;
    $[35] = isPositionRiskyAcknowledged;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <span className="text-3xs text-secondary-foreground">By using this feature, you agree to the{" "}<ExternalLink href={TOU_URL} underlined={true}>Terms of Use</ExternalLink>{" "}and acknowledge that you are responsible for monitoring your own position.</span>;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== isAbovePreLltv || $[39] !== needToAcknowledgePositionRisky || $[40] !== openReviewDialog || $[41] !== toEnable) {
    t17 = toEnable ? <Button size="lg" onClick={openReviewDialog} disabled={isAbovePreLltv || needToAcknowledgePositionRisky}>Enable Auto-Deleverage</Button> : <Button variant="destructive" size="lg" onClick={openReviewDialog} disabled={!isAbovePreLltv && needToAcknowledgePositionRisky}>Disable Auto-Deleverage</Button>;
    $[38] = isAbovePreLltv;
    $[39] = needToAcknowledgePositionRisky;
    $[40] = openReviewDialog;
    $[41] = toEnable;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="flex justify-center p-[6px_7px]"><ExternalLink href={AUTO_DELEVERAGE_DOCS_URL}><Button variant="ghost">Learn more<ArrowDiagonalUp /></Button></ExternalLink></div>;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t13 || $[47] !== t14 || $[48] !== t15 || $[49] !== t17) {
    t19 = <div className="gap-lg flex flex-col">{t10}{t11}{t13}{t14}{t15}{t16}{t17}{t18}</div>;
    $[44] = t10;
    $[45] = t11;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t17;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  return t19;
};
export default memo(AutoDeleverageDialog);